import {
  cloneElement, useState, useMemo, useEffect, useCallback,
  useImperativeHandle, forwardRef, ForwardedRef,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { queryFilters } from '../../../utils/queryFilters';
import { pageToQuery } from '../../../utils/pageIntoQueryParams';
import { FilterConfig } from '../DropdownFilter/types';
import ItemsListSkeleton from '../Skeleton/ItemsListSkeleton';
import DropdownFilter from '../DropdownFilter/DropdownFilter';
import SearchFilter from '../SearchFilter/SearchFilter';
import Pagination from '../Pagination/Pagination';
import S from './ItemsListStyles';
import { H2 } from '../../ui/H2/H2';
import { Props, DataDetails } from './types';
import { Button } from '../../ui/Button/Button';
import { ThemeType } from '../../../theme/themeType';
import useGetDriversAvatarsBulk from '../../../API/hooks/drivers/useGetDriversAvatarsBulk';
import { DriverAvatar } from '../../../API/types/Drivers';
import { logger } from '../../../API/services/SharedService';
import { VehicleDetails } from '../../../API/types/Vehicles';
import ReportRequest from '../ReportRequest/ReportRequest';
import { FeaturesManagement } from '../../../featuresControl';
import Icon from '../Icon/Icon';
import ResetFilter from '../ResetFilter/ResetFilter';
import {
  buildListNavigationEntries,
  consumeListItemFocus,
  createListNavigationContextKey,
  isListNavigationContextComplete,
  ListNavigationEntity,
  ListNavigationSourceItem,
  saveListNavigationContext,
} from '../../../utils/listNavigation';

const LIST_NAVIGATION_MAX_ITEMS = 500;

const generateKey = (item: DataDetails): string => `${item._id} ${item?.updatedAt ? item.updatedAt : ''}`;

const getAvatarByDriverId = (avatars: DriverAvatar[], driverId: string): string | null => {
  const driversAvatars = avatars.filter((d: any) => d.driverId === driverId);
  return driversAvatars.length ? driversAvatars[0].avatarUrl : null;
};

const getTitleByEntity = (data: any, entity: string, externalTitle = ''): string => {
  const showEntities = ['alerts', 'drivers', 'vehicles', 'devices', 'trips'];
  const total = data?.total || 0;
  const displayEntity = total === 1 && entity.endsWith('s') ? entity.slice(0, -1) : entity;
  let title = '';
  if (showEntities.includes(entity)) {
    title = `${total} ${displayEntity}`;
  }
  if (externalTitle) {
    title = title ? `${title} • ${externalTitle}` : externalTitle;
  }

  return title;
};

const getDateFieldByEntity = (entity: string): string | undefined => ({
  alerts: 'eventDate',
  trips: 'startAt',
})[entity];

const getDateValue = (value?: string): number => {
  if (!value) {
    return 0;
  }
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getDevicesSortedByActiveAtWithOnlinePriority = (
  items: DataDetails[],
  sortBy: string,
): DataDetails[] => {
  const isAsc = sortBy.endsWith('_ASC');
  const isOnline = (item: DataDetails): boolean => Boolean((item as any).isOnline);

  const sortByActiveAt = (a: DataDetails, b: DataDetails): number => {
    const aTime = getDateValue((a as any).activeAt);
    const bTime = getDateValue((b as any).activeAt);
    return isAsc ? aTime - bTime : bTime - aTime;
  };

  const onlineItems = items.filter(isOnline).sort(sortByActiveAt);
  const offlineItems = items.filter((item) => !isOnline(item)).sort(sortByActiveAt);

  return isAsc ? [...offlineItems, ...onlineItems] : [...onlineItems, ...offlineItems];
};

const getDriverName = (item: DataDetails): { firstName: string; lastName: string } => ({
  firstName: String((item as any).firstName || '').trim(),
  lastName: String((item as any).lastName || '').trim(),
});

const PERIOD_TO_SCORE_FIELD: Record<string, string> = {
  daily: 'scoreDaily',
  weekly: 'scoreWeekly',
  monthly: 'scoreMonthly',
};

const getScoreFieldForPeriod = (period?: string): string => PERIOD_TO_SCORE_FIELD[period ?? ''] ?? 'score';

const hasNoDriverScore = (item: DataDetails, scoreField: string): boolean => {
  const rawScore = (item as any)[scoreField];
  const score = Number(rawScore);
  return rawScore === null || rawScore === undefined || Number.isNaN(score) || score === 0;
};

const compareDriversAlphabetically = (a: DataDetails, b: DataDetails): number => {
  const driverA = getDriverName(a);
  const driverB = getDriverName(b);

  const firstNameComparison = driverA.firstName.localeCompare(
    driverB.firstName,
    undefined,
    { sensitivity: 'base' },
  );
  if (firstNameComparison !== 0) {
    return firstNameComparison;
  }

  return driverA.lastName.localeCompare(driverB.lastName, undefined, { sensitivity: 'base' });
};

const compareByLicensePlate = (a: DataDetails, b: DataDetails): number => {
  const plateA = String((a as any).licensePlateNumber || '');
  const plateB = String((b as any).licensePlateNumber || '');
  return plateA.localeCompare(plateB, undefined, { sensitivity: 'base' });
};

const getItemsSortedByScoreWithNoScoreGrouping = (
  items: DataDetails[],
  sortBy: string,
  entity: string,
  period?: string,
): DataDetails[] => {
  const isAsc = sortBy.endsWith('_ASC');
  const scoreField = getScoreFieldForPeriod(period);

  return [...items].sort((a: DataDetails, b: DataDetails): number => {
    const noScoreA = hasNoDriverScore(a, scoreField);
    const noScoreB = hasNoDriverScore(b, scoreField);

    if (noScoreA !== noScoreB) {
      return noScoreA ? 1 : -1;
    }

    if (!noScoreA && !noScoreB) {
      const scoreA = Number((a as any)[scoreField]);
      const scoreB = Number((b as any)[scoreField]);
      if (scoreA !== scoreB) {
        return isAsc ? scoreA - scoreB : scoreB - scoreA;
      }
    }

    return entity === 'vehicles'
      ? compareByLicensePlate(a, b)
      : compareDriversAlphabetically(a, b);
  });
};

export interface ItemsListRef {
  clearSelection: () => void;
  toggleSelection: (enabled?: boolean) => void;
}

const ItemsList = forwardRef(({
  fleetId,
  entity,
  title,
  customQuery,
  filtersConfig,
  children,
  getQuery,
  isRow,
  filters,
  defaultFilters,
  setFilters,
  groupFilters,
  hasSearch,
  hasCreateReport,
  reportEndpoint,
  reportName,
  buttons,
  onSelect,
  customPageLimit,
  curentPage = 1,
  scoreOverrides,
  alertCountOverrides,
  toolbarExtra,
}: Props, ref: ForwardedRef<ItemsListRef>): JSX.Element => {
  const theme = useTheme() as ThemeType;
  const navigate = useNavigate();
  const [page, setPage] = useState(curentPage);
  const location = useLocation();
  const [avatars, setAvatars] = useState<DriverAvatar[]>([]);
  const [localFilters, localSetFilters] = useState<Record<string, string[]>>({});
  const [isSelectionMode, setSelectionMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const effectivePeriod = filters?.period?.[0];
  const allFilters = useMemo(() => {
    const base = { ...(filters || localFilters || {}), ...(groupFilters || null) };
    const sortByValue = base.sortBy?.[0];
    if (sortByValue === 'score_ASC' || sortByValue === 'score_DESC') {
      const scoreField = getScoreFieldForPeriod(effectivePeriod);
      const dir = sortByValue.endsWith('_ASC') ? 'ASC' : 'DESC';
      return { ...base, sortBy: [`${scoreField}_${dir}`] };
    }
    return base;
  }, [filters, groupFilters, localFilters, effectivePeriod]);
  const sortBy = allFilters?.sortBy?.[0];
  const onlinePrioritySortKeys = [
    'activeAt_ASC',
    'activeAt_DESC',
    'isOnline_ASC',
    'isOnline_DESC',
  ];
  const driverScoreSortKeys = [
    'score_ASC', 'score_DESC',
    'scoreDaily_ASC', 'scoreDaily_DESC',
    'scoreWeekly_ASC', 'scoreWeekly_DESC',
    'scoreMonthly_ASC', 'scoreMonthly_DESC',
  ];
  const shouldApplyDevicesOnlinePrioritySort = entity === 'devices'
    && !!sortBy
    && onlinePrioritySortKeys.includes(sortBy);
  const shouldApplyDriversNoScoreGroupingSort = (entity === 'drivers' || entity === 'vehicles')
    && !!sortBy
    && driverScoreSortKeys.includes(sortBy);
  const supportsListNavigation = ['alerts', 'trips', 'drivers', 'devices'].includes(entity);
  // For device online/last-active sorting we must sort before pagination.
  // We therefore fetch one larger dataset and paginate client-side.
  const shouldUseClientPagination = shouldApplyDevicesOnlinePrioritySort
    || shouldApplyDriversNoScoreGroupingSort;
  const queryPage = shouldUseClientPagination ? undefined : page;
  const { data, isLoading, isFetching } = getQuery(
    fleetId,
    queryFilters(allFilters, queryPage, customQuery, getDateFieldByEntity(entity), customPageLimit),
  );
  const navigationQuery = supportsListNavigation && !shouldUseClientPagination
    ? queryFilters(
      allFilters,
      undefined,
      customQuery,
      getDateFieldByEntity(entity),
      LIST_NAVIGATION_MAX_ITEMS,
    )
    : queryFilters(
      allFilters,
      queryPage,
      customQuery,
      getDateFieldByEntity(entity),
      customPageLimit,
    );
  const sortedResults = useMemo(() => {
    const rawResults = ((data?.results || []) as DataDetails[]).map((item) => {
      let merged = item;
      if (scoreOverrides) {
        const override = scoreOverrides.get(item._id);
        merged = override ? { ...merged, ...override } as DataDetails : merged;
      }
      if (alertCountOverrides) {
        const override = alertCountOverrides.get(item._id);
        merged = override ? { ...merged, ...override } as DataDetails : merged;
      }
      return merged;
    });
    if (!sortBy) {
      return rawResults;
    }

    if (shouldApplyDriversNoScoreGroupingSort) {
      return getItemsSortedByScoreWithNoScoreGrouping(rawResults, sortBy, entity, effectivePeriod);
    }

    if (!shouldApplyDevicesOnlinePrioritySort) {
      return rawResults;
    }

    return getDevicesSortedByActiveAtWithOnlinePriority(rawResults, sortBy);
  }, [
    data?.results,
    shouldApplyDevicesOnlinePrioritySort,
    shouldApplyDriversNoScoreGroupingSort,
    sortBy,
    effectivePeriod,
    entity,
    scoreOverrides,
    alertCountOverrides,
  ]);
  const currentPageLimit = customPageLimit || 20;
  const results = useMemo(() => {
    if (!shouldUseClientPagination) {
      return sortedResults;
    }

    const { skip, limit } = pageToQuery(page, currentPageLimit);
    return sortedResults.slice(skip, skip + limit);
  }, [currentPageLimit, page, shouldUseClientPagination, sortedResults]);
  const listNavigationContextKey = createListNavigationContextKey(
    entity as ListNavigationEntity,
    location.pathname,
    location.search,
    JSON.stringify({ allFilters, customQuery, currentPageLimit }),
  );
  useEffect(() => {
    if (!supportsListNavigation) return;
    const hasFullList = shouldUseClientPagination && !!sortedResults.length;
    const contextItems = hasFullList ? sortedResults : results;
    if (!contextItems.length) return;
    if (!hasFullList && isListNavigationContextComplete(listNavigationContextKey)) return;
    saveListNavigationContext(listNavigationContextKey, {
      entity: entity as ListNavigationEntity,
      returnPath: `${location.pathname}${location.search}`,
      returnState: location.state,
      pageSize: currentPageLimit,
      isComplete: hasFullList,
      fleetId,
      query: navigationQuery,
      entries: buildListNavigationEntries(
        contextItems as unknown as ListNavigationSourceItem[],
        entity as ListNavigationEntity,
        currentPageLimit,
        hasFullList ? undefined : page,
      ),
    });
  }, [
    currentPageLimit,
    entity,
    fleetId,
    listNavigationContextKey,
    location.pathname,
    location.search,
    location.state,
    navigationQuery,
    page,
    results,
    shouldUseClientPagination,
    sortedResults,
    supportsListNavigation,
  ]);

  useEffect(() => {
    if (!supportsListNavigation || !results.length) return;
    const returnPath = `${location.pathname}${location.search}`;
    const itemId = consumeListItemFocus(returnPath);
    if (!itemId) return;
    const element = Array.from(
      document.querySelectorAll<HTMLElement>('[data-list-item-id]'),
    ).find((candidate) => candidate.dataset.listItemId === itemId);
    if (!element) return;
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [
    location.pathname,
    location.search,
    results,
    supportsListNavigation,
  ]);
  useEffect(() => {
    if (!shouldUseClientPagination || !data?.total) {
      return;
    }

    if (data.total > sortedResults.length) {
      logger.warning(
        'itemsList/client-sort',
        'Client-side sort is limited by fetched dataset size',
        {
          entity,
          sortBy,
          totalFromBackend: data.total,
          fetchedCount: sortedResults.length,
        },
      );
    }
  }, [data?.total, shouldUseClientPagination, sortBy, sortedResults.length, entity]);
  useEffect(() => {
    if (onSelect) {
      onSelect(selectedItems);
    }
  }, [onSelect, selectedItems]);

  useImperativeHandle(ref, () => ({
    clearSelection: () => {
      setSelectedItems([]);
      if (onSelect) {
        onSelect([]);
      }
    },
    toggleSelection: (enabled?: boolean) => {
      setSelectedItems([]);
      setSelectionMode((state) => enabled || !state);
      if (onSelect) {
        onSelect([]);
      }
    },
  }));

  const navigateToPage = useCallback((targetPage: number): void => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', targetPage.toString());
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
    setPage(targetPage);
  }, [location.pathname, location.search, navigate]);

  const changePage = (paginationAction: any): void => {
    const targetPage = paginationAction(curentPage);
    if (targetPage) {
      navigateToPage(targetPage);
    }
  };

  const hasNextPage = shouldUseClientPagination
    ? page * currentPageLimit < sortedResults.length
    : (data?.total && (data.limit + data.skip + 1) <= data.total) || data?.nextPage;
  const hasPrevPage = shouldUseClientPagination
    ? page > 1
    : (data?.skip && data.skip > 0) || data?.prevPage;
  let totalPageNumber = 0;
  if (shouldUseClientPagination) {
    totalPageNumber = Math.ceil(sortedResults.length / currentPageLimit);
  } else if (data?.total && data?.limit > 0) {
    totalPageNumber = Math.ceil(data.total / data.limit);
  }

  let currentPageNumber = curentPage;
  if (shouldUseClientPagination) {
    currentPageNumber = page;
  } else if (data?.skip && data?.limit > 0) {
    currentPageNumber = Math.floor(data.skip / data.limit) + 1;
  }

  useEffect(() => {
    if (totalPageNumber !== 0 && currentPageNumber > totalPageNumber) {
      navigateToPage(totalPageNumber > 0 ? totalPageNumber : 1);
    } else if (
      totalPageNumber === 0
      && !isLoading
      && !data?.results?.length
      && currentPageNumber > 1
    ) {
      navigateToPage(1);
    }
  }, [
    currentPageNumber,
    data?.results?.length,
    isLoading,
    navigateToPage,
    totalPageNumber,
  ]);

  const pageTitle = getTitleByEntity(data, entity, title);

  const onSubmitDriversAvatarsSuccess = (a: DriverAvatar[]): void => {
    setAvatars(a);
  };
  const onSubmitDriversAvatarsFail = (err: string): void => {
    logger.error('getDriversAvatarsBulk', 'On get fail', err);
  };

  const { mutate: getDriversAvatarsBulk, isLoading: isDriversAvatarsBulkLoading } = useGetDriversAvatarsBulk(
    onSubmitDriversAvatarsSuccess,
    onSubmitDriversAvatarsFail,
  );

  const extendDriverProperties = (item: any): any => {
    const obj = item;
    if (obj.driver) {
      obj.driver.avatarUrl = getAvatarByDriverId(avatars, (item as VehicleDetails).driverId);
    }
    return obj;
  };

  if (
    !isLoading
    && !isDriversAvatarsBulkLoading
    && data
    && fleetId
    && (entity === 'vehicles' || entity === 'trips')
    && !avatars.length) {
    const vehiclesIds = data.results?.filter((v: any) => v.driverId).map((v: any) => v.driverId);
    if (vehiclesIds && vehiclesIds.length) {
      getDriversAvatarsBulk({
        fleetId,
        driverIds: vehiclesIds,
      });
    }
  }

  const toggleSelectionMode = (): void => {
    setSelectedItems([]);
    setSelectionMode((state) => !state);
    if (onSelect) {
      onSelect([]);
    }
  };

  const handleSelectItem = (isChecked: boolean, itemId: string): void => (
    setSelectedItems((state) => (
      !isChecked ? [...state, itemId] : state.filter((id) => id !== itemId)
    ))
  );

  return (
    <S.Wrapper>
      <div className="title-container">
        <H2> {pageTitle} </H2>
        {(buttons && isSelectionMode) && buttons.filter((button) => !button.hide).map((button) => (
          <Button
            key={button.title}
            background={button.background}
            hoverBackground={button.hoverBackground}
            hoverColor={button.hoverColor}
            height={button.height}
            onClick={button.action}
            textColor={button.textColor}
            disabled={button.disabled !== undefined ? button.disabled : false}
          >
            {button.iconKey && <Icon iconKey={button.iconKey} />}
            {button.title}
          </Button>
        ))}
        {!!buttons?.length && (
          <>
            {!isSelectionMode && (
              <Button
                height="25px"
                gap="0px"
                onClick={() => {
                  toggleSelectionMode();
                }}
                background={theme.colors.red}
                hoverBackground={theme.colors.red}
              >
                Select
              </Button>
            )}
            {isSelectionMode && (
              <Button
                height="25px"
                gap="0px"
                onClick={toggleSelectionMode}
                background={theme.colors.green}
                hoverBackground={theme.colors.green}
                textColor={theme.colors.text}
                hoverColor={theme.colors.text}
              >
                Cancel
              </Button>
            )}
          </>
        )}
        {hasSearch && (
          <SearchFilter
            filters={filters || localFilters}
            setFilters={setFilters || localSetFilters}
          />
        )}
        {hasCreateReport && FeaturesManagement.features.report && (
          <ReportRequest
            fleetId={fleetId}
            name={reportName}
            endpoint={reportEndpoint}
            query={queryFilters(
              allFilters,
              page,
              customQuery,
              entity === 'alerts' ? 'eventDate' : undefined,
              customPageLimit,
            )}
          />
        )}
        {defaultFilters && (
          <ResetFilter
            filters={filters || localFilters}
            defaultFilters={defaultFilters}
            setFilters={setFilters || localSetFilters}
          />
        )}
        {toolbarExtra}
        {
          filtersConfig.map((filterConfig: FilterConfig) => (
            <DropdownFilter
              key={filterConfig.type}
              config={filterConfig}
              filters={filters || localFilters}
              defaultFilters={defaultFilters}
              setFilters={setFilters || localSetFilters}
              showValue={true as boolean}
            />
          ))
        }
      </div>
      {isLoading && <ItemsListSkeleton />}
      {!isLoading && data?.results && (
        <>
          <ul className={`list-container ${isRow ? 'row' : ''}`}>
            {!!results.length
              && results
                .map((item: DataDetails) => cloneElement(children, {
                  key: generateKey(item),
                  item: (entity === 'vehicles' || entity === 'trips') ? extendDriverProperties(item) : item,
                  isSelecting: isSelectionMode,
                  onSelect: handleSelectItem,
                  isSelected: selectedItems.includes(item._id),
                  period: effectivePeriod,
                  filters,
                  listNavigation: supportsListNavigation ? {
                    contextKey: listNavigationContextKey,
                    itemId: item._id,
                  } : undefined,
                }))}
            {!isLoading && !results.length && <div className="no-data">No {entity} found</div>}
          </ul>

          <Pagination
            setPage={changePage}
            nextPage={hasNextPage}
            prevPage={hasPrevPage}
            currentPage={currentPageNumber}
            totalPages={totalPageNumber}
            isFetching={isFetching}
          />
        </>
      )}
    </S.Wrapper>
  );
});

export default ItemsList;
